import React, { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { allProductsData as allProducts } from '../data/products';
import './ProductsList.css';

const PRODUCTS_PER_PAGE = 9;

const ProductsList = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
    const totalPages = Math.ceil(allProducts.length / PRODUCTS_PER_PAGE);
    const sectionRef = useRef(null);
    const isFirstRender = useRef(true);

    const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
    const currentProducts = allProducts.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);

    // Scroll to top of products section AFTER the new page renders.
    // Skip on first render so arriving via navbar shows the hero section at top.
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (sectionRef.current) {
            sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [currentPage]);

    const handleNext = () => {
        if (currentPage < totalPages) {
            setSearchParams({ page: currentPage + 1 });
        }
    };

    const handlePrev = () => {
        if (currentPage > 1) {
            setSearchParams({ page: currentPage - 1 });
        }
    };

    return (
        <section className="products-list-section" ref={sectionRef}>
            <div className="products-list-container">

                <div className="products-list-header">
                    <div className="products-header-left">
                        <span className="section-subtitle">EQUIPMENT</span>
                        <h2 className="products-main-title">
                            Our Physiotherapy &<br />
                            Rehabilitation Equipment
                        </h2>
                    </div>
                </div>

                <div className="products-grid-wrapper">
                    {currentProducts.map(product => {
                        const heightRange = product.specs.find(s => s.label.includes('HEIGHT'))?.value ||
                                           product.detailedSpecs?.find(s => s.label.includes('Height'))?.value;

                        return (
                            <Link to={`/product/${product.slug || product.id}`} className="product-card-vertical" key={product.id} style={{ textDecoration: 'none', color: 'inherit' }}>
                                <div className="product-image-wrapper">
                                    <div className="product-image-container">
                                        <img src={product.image} alt={product.title} />
                                    </div>
                                    <div className="view-icon-btn" aria-label={`View details for ${product.title}`}>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="7" y1="17" x2="17" y2="7"></line>
                                            <polyline points="7 7 17 7 17 17"></polyline>
                                        </svg>
                                    </div>
                                </div>

                                <div className="product-details-content">
                                    <div className="product-details-header">
                                        <h3 className="product-name">{product.title}</h3>
                                        {heightRange && (
                                            <div className="height-range-info">
                                                <span className="info-label">Height Range</span>
                                                <span className="info-value">{heightRange.split('[cite:')[0]}</span>
                                            </div>
                                        )}
                                    </div>

                                    <ul className="product-features-list">
                                        {(product.keyFeatures || []).slice(0, 5).map((feature, idx) => (
                                            <li key={idx}>
                                                <span className="bullet">•</span>
                                                {feature.split('[cite:')[0].trim()}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Link>
                        );
                    })}
                </div>

                <div className="products-pagination">
                    <div className="pagination-info">
                        {currentPage} / {totalPages}
                    </div>
                    <div className="pagination-controls">
                        <button
                            className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`}
                            onClick={handlePrev}
                            disabled={currentPage === 1}
                        >
                            &lsaquo; Prev
                        </button>
                        <button
                            className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`}
                            onClick={handleNext}
                            disabled={currentPage === totalPages}
                        >
                            Next &rsaquo;
                        </button>
                    </div>
                </div>

            </div>
        </section>
    );
};

export default ProductsList;
